import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/Button";
import { SelectField, TextField } from "@/components/Field";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { call, CALLABLES } from "@/services/callables";
import { AGE_BAND_CONSEQUENCE_KEY, ageBandConsequence } from "./rules";
import { SELECTABLE_AGE_BANDS } from "../people/review";
import type { AgeBand, AssessmentPreset, Group, SessionMode } from "@/types";

/**
 * What a group is.
 *
 * The age band is editable, and changing it moves the group in or out of the
 * parental-consent regime — which the backend records in the audit log, because
 * it is a compliance fact rather than a preference. The warning is shown here
 * for the same reason it is shown when the group is created.
 */
export function SettingsTab({ group }: { group: Group }) {
  const t = useT();
  const { success, showError } = useToast();
  const { institution } = useAuth();

  const [name, setName] = useState(group.name);
  const [ageBand, setAgeBand] = useState<AgeBand>(group.ageBand);
  const [defaultMode, setDefaultMode] = useState<SessionMode>(group.defaultMode);
  const [preset, setPreset] = useState<AssessmentPreset | "">("");
  const [busy, setBusy] = useState(false);

  // Re-seeded when the group changes underneath us — a colleague renaming it in
  // another tab should not leave this form editing a name nobody has any more.
  useEffect(() => {
    setName(group.name);
    setAgeBand(group.ageBand);
    setDefaultMode(group.defaultMode);
  }, [group.id, group.name, group.ageBand, group.defaultMode]);

  // The country decides whether a minor needs a parent to consent, so the
  // sentence under the field cannot be written from the band alone.
  const consequence = ageBandConsequence(ageBand, institution?.country ?? "");

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await call(CALLABLES.updateGroup, {
        groupId: group.id,
        name: name.trim(),
        ageBand,
        defaultMode,
        ...(preset ? { assessmentPresetOverride: preset } : {}),
      });
      success("common.saved");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (archived: boolean) => {
    setBusy(true);
    try {
      await call(CALLABLES.archiveGroup, { groupId: group.id, archived });
      success("common.saved");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <form className="flex flex-col gap-5" onSubmit={save}>
        <TextField
          labelKey="group.name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <div>
          <SelectField
            labelKey="group.ageBand"
            value={ageBand}
            onChange={(event) => setAgeBand(event.target.value as AgeBand)}
            options={[
              ...SELECTABLE_AGE_BANDS.map((band) => ({
                value: band,
                labelKey: `import.ageBand.${band}`,
              })),
              { value: "mixed", labelKey: "import.ageBand.mixed" },
            ]}
          />
          <p
            className={`mt-2 rounded-card px-4 py-3 text-sm ${
              consequence === "minorsConsent"
                ? "border border-warning bg-warning-soft text-ink-soft"
                : "text-ink-muted"
            }`}
          >
            {t(AGE_BAND_CONSEQUENCE_KEY[consequence])}
          </p>
        </div>

        {/*
          "How lessons connect: Online / Offline" told a teacher nothing about
          what it changes (live run, 2026-09-09). The question is where the
          class is; the consequence is whether the room's kit is needed.
        */}
        <SelectField
          labelKey="group.defaultMode"
          hintKey="group.defaultModeHint"
          value={defaultMode}
          onChange={(event) => setDefaultMode(event.target.value as SessionMode)}
          options={[
            { value: "online", labelKey: "group.mode.online" },
            { value: "offline", labelKey: "group.mode.offline" },
          ]}
        />

        <SelectField
          labelKey="group.settings.assessmentOverride"
          hintKey="group.settings.assessmentHint"
          value={preset}
          onChange={(event) => setPreset(event.target.value as AssessmentPreset | "")}
          options={[
            { value: "", labelKey: "group.settings.assessmentInherit" },
            { value: "epa", labelKey: "institution.preset.epa" },
            { value: "aoao", labelKey: "institution.preset.aoao" },
            { value: "ncas", labelKey: "institution.preset.ncas" },
            { value: "custom", labelKey: "institution.preset.custom" },
          ]}
        />

        <div>
          <Button type="submit" busy={busy} disabled={name.trim().length === 0}>
            {t("common.save")}
          </Button>
        </div>
      </form>

      <div className="mt-10 border-t border-line pt-6">
        <p className="text-sm text-ink-soft">{t("group.settings.archiveBody")}</p>
        <Button
          variant="secondary"
          busy={busy}
          className="mt-3"
          onClick={() => archive(!group.archivedAt)}
        >
          {group.archivedAt ? t("group.settings.unarchive") : t("group.settings.archive")}
        </Button>
      </div>
    </div>
  );
}
